import React from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function TalentAnalyticsDashboard({ talentData }) {
  const { t } = useTranslation();

  if (!talentData || talentData.length === 0) {
    return (
      <div className="card">
        <p>{t('sharedTalentPool.noTalentCardsShared', 'No talent data available to generate analytics.')}</p>
      </div>
    );
  }

  // --- Data Processing ---

  const calculateExperienceYears = (duration) => {
    if (!duration || typeof duration !== 'string') return 0;
  
    const parts = duration.split(' - ');
    if (parts.length !== 2) return 0;
  
    const [startStr, endStr] = parts;
    const startDate = new Date(startStr);
    const endDate = (endStr.toLowerCase() === 'present') ? new Date() : new Date(endStr);
  
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      const yearRegex = /\d{4}/g;
      const startMatch = startStr.match(yearRegex);
      const endMatch = endStr.match(yearRegex);
      if (startMatch && endMatch) {
        return parseInt(endMatch[0], 10) - parseInt(startMatch[0], 10);
      }
      return 0;
    }
  
    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();
  
    if (months < 0 || (months === 0 && endDate.getDate() < startDate.getDate())) {
      years--;
    }
  
    return years;
  };

  const getTotalExperience = (talentCard) => {
    if (!talentCard.experience || !Array.isArray(talentCard.experience)) return 0;
    return talentCard.experience.reduce((total, job) => total + calculateExperienceYears(job.duration), 0);
  };

  const talentDataWithExperience = talentData.map(card => ({
    ...card,
    totalExperience: getTotalExperience(card),
  }));

  // 1. KPIs
  const totalTalent = talentData.length;
  
  const skillCounts = talentData.flatMap(card => card.skills || []).reduce((acc, skill) => {
    acc[skill] = (acc[skill] || 0) + 1;
    return acc;
  }, {});
  const topSkillEntry = Object.entries(skillCounts).sort(([,a],[,b]) => b-a)[0] || ['N/A', 0];
  const topSkill = { name: topSkillEntry[0], percentage: totalTalent > 0 ? ((topSkillEntry[1] / totalTalent) * 100).toFixed(0) : 0 };

  const totalExperienceYears = talentDataWithExperience.reduce((acc, card) => acc + card.totalExperience, 0);
  const avgExperience = totalTalent > 0 ? (totalExperienceYears / totalTalent).toFixed(1) : 0;

  const readyToMoveCount = talentData.filter(card => 
    card.availability && (
      card.availability.toLowerCase().includes('open to opportunities') ||
      card.availability.toLowerCase().includes('actively looking')
    )
  ).length;
  const readyToMove = totalTalent > 0 ? `${((readyToMoveCount / totalTalent) * 100).toFixed(0)}%` : '0%';

  // 2. Skill Chart
  const topSkills = Object.entries(skillCounts).sort(([,a],[,b]) => b-a).slice(0, 10);
  const skillChartData = {
    labels: topSkills.map(([skill]) => skill),
    datasets: [{
      label: t('talentAnalytics.numberOfCandidates', 'Number of Candidates'),
      data: topSkills.map(([, count]) => count),
      backgroundColor: 'rgba(0, 82, 204, 0.7)',
      borderColor: 'rgba(0, 82, 204, 1)',
      borderWidth: 1,
    }],
  };

  // 3. Experience Chart
  const expCounts = { entry: 0, mid: 0, senior: 0, lead: 0 };
  
  talentDataWithExperience.forEach(card => {
    const exp = card.totalExperience;
    if (exp <= 2) expCounts.entry++;
    else if (exp <= 5) expCounts.mid++;
    else if (exp <= 10) expCounts.senior++;
    else expCounts.lead++;
  });

  const experienceChartData = {
    labels: [
      t('talentAnalytics.entryLevel', 'Entry-Level (0-2 yrs)'),
      t('talentAnalytics.midLevel', 'Mid-Level (3-5 yrs)'),
      t('talentAnalytics.seniorLevel', 'Senior (6-10 yrs)'),
      t('talentAnalytics.leadLevel', 'Lead/Principal (10+ yrs)')
    ],
    datasets: [{
      data: [expCounts.entry, expCounts.mid, expCounts.senior, expCounts.lead],
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'],
    }],
  };
  
  // 4. Top Strengths
  const strengthCounts = talentData.flatMap(card => card.strengths || []).reduce((acc, strength) => {
    acc[strength] = (acc[strength] || 0) + 1;
    return acc;
  }, {});
  const topStrengths = Object.entries(strengthCounts).sort(([,a],[,b]) => b-a).slice(0, 7);

  // 5. Top Languages
  const languageCounts = talentData.flatMap(card => card.languages || []).reduce((acc, lang) => {
    acc[lang] = (acc[lang] || 0) + 1;
    return acc;
  }, {});
  const topLanguages = Object.entries(languageCounts).sort(([,a],[,b]) => b-a).slice(0, 5);
  const languageChartData = {
    labels: topLanguages.map(([lang]) => lang),
    datasets: [{
      label: t('talentAnalytics.numberOfSpeakers', 'Number of Speakers'),
      data: topLanguages.map(([, count]) => count),
      backgroundColor: 'rgba(75, 192, 192, 0.7)',
    }],
  };

  // 6. Top Certifications
  const certificationCounts = talentData.flatMap(card => card.certifications?.map(c => c.name) || []).reduce((acc, cert) => {
    if(cert) acc[cert] = (acc[cert] || 0) + 1;
    return acc;
  }, {});
  const topCertifications = Object.entries(certificationCounts).sort(([,a],[,b]) => b-a).slice(0, 5);
  const certificationChartData = {
    labels: topCertifications.map(([cert]) => cert),
    datasets: [{
      label: t('talentAnalytics.numberOfHolders', 'Number of Holders'),
      data: topCertifications.map(([, count]) => count),
      backgroundColor: 'rgba(255, 159, 64, 0.7)',
    }],
  };

  // --- Chart Options ---

  // Generic callback to adjust y-axis width for long labels
  const yAxisAfterFit = (axis) => {
    const longestLabel = axis.ticks.reduce((longest, tick) => {
      const label = tick.label;
      return label.length > longest.length ? label : longest;
    }, '');
    
    const ctx = axis.ctx;
    ctx.font = axis.font;
    const textMetrics = ctx.measureText(longestLabel);
    
    axis.width = textMetrics.width + 20; // Add 20px buffer
  };

  const languageChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: { 
      y: { 
        beginAtZero: true,
        ticks: {
          font: {
            size: 10
          }
        }
      } 
    },
    plugins: { legend: { display: false } }
  };

  const certificationChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: { 
      y: { 
        beginAtZero: true,
        ticks: {
          font: {
            size: 10
          }
        }
      } 
    },
    plugins: { legend: { display: false } }
  };

  // 7. Top Values
  const valueCounts = talentData.flatMap(card => card.values || []).reduce((acc, value) => {
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
  const topValues = Object.entries(valueCounts).sort(([,a],[,b]) => b-a).slice(0, 7);

  // 8. Location Preferences
  const normalizeLocation = (loc) => {
    if (!loc) return null;
    const lower = loc.toLowerCase();
    
    // Pattern matching for specific long strings
    if (lower.includes('relocat') || lower.includes('opportunity') || lower.includes('aligns with my vision')) {
        return t('talentAnalytics.willingToRelocate', 'Willing to relocate');
    }
    
    if (lower.includes('vize') || lower.includes('visa')) {
        // Specific catch for the complex visa string: "Uk'de calisma vizemvar. US ve AB icin..."
        if (lower.includes('us') && (lower.includes('ab') || lower.includes('eu'))) {
             return "US, EU, UK";
        }
        return t('talentAnalytics.visaRequired', 'Visa Sponsorship');
    }
    
    // Catch-all for other long descriptions that aren't caught above
    if (loc.length > 30) return t('talentAnalytics.flexible', 'Flexible');

    return loc;
  };

  const locationCounts = talentData.flatMap(card => 
      card.locationPreferences ? card.locationPreferences.split(',').map(l => normalizeLocation(l.trim())).filter(Boolean) : []
  ).reduce((acc, loc) => {
    acc[loc] = (acc[loc] || 0) + 1;
    return acc;
  }, {});
  const topLocations = Object.entries(locationCounts).sort(([,a],[,b]) => b-a).slice(0, 5);
  const locationChartData = {
    labels: topLocations.map(([loc]) => loc),
    datasets: [{
      label: t('talentAnalytics.numberOfCandidates', 'Number of Candidates'),
      data: topLocations.map(([, count]) => count),
      backgroundColor: 'rgba(153, 102, 255, 0.7)',
    }],
  };

  // 9. Ideal Work Environment Insights
  const keywordMap = {
    'innovation': ['innovation', 'innovative'],
    'autonomy': ['autonomy', 'autonomous'],
    'collaboration': ['collaboration', 'collaborative'],
    'fast-paced': ['fast-paced'],
    'structured': ['structured'],
    'flexible': ['flexible', 'flexibility'],
    'remote': ['remote'],
    'hybrid': ['hybrid'],
    'impact': ['impact', 'impactful'],
    'growth': ['growth', 'growing'],
    'learning': ['learning', 'learn'],
    'mentorship': ['mentorship', 'mentor'],
    'diversity': ['diverse', 'diversity'],
    'inclusion': ['inclusive', 'inclusion'],
    'stability': ['stable', 'stability'],
    'mission-driven': ['mission-driven', 'mission'],
    'work-life balance': ['work-life balance', 'balance'],
    'strategic': ['strategic', 'strategy'],
    'creative': ['creative', 'creativity'],
    'analytical': ['analytical', 'analysis'],
  };

  const keywordCounts = talentData.reduce((acc, card) => {
    const text = (card.idealWorkEnvironment || '').toLowerCase();
    if (!text) return acc;

    for (const [baseKeyword, variants] of Object.entries(keywordMap)) {
      if (variants.some(variant => text.includes(variant))) {
        acc[baseKeyword] = (acc[baseKeyword] || 0) + 1;
      }
    }
    return acc;
  }, {});
  const topKeywords = Object.entries(keywordCounts).sort(([,a],[,b]) => b-a).slice(0, 7);

  // Helper function for dynamic tag styling
  const getTagStyle = (count, minCount, maxCount) => {
    const minSize = 12; // min font size in px
    const maxSize = 24; // max font size in px
    const minWeight = 300;
    const maxWeight = 550;

    if (minCount === maxCount) {
      return { fontSize: `${minSize}px`, fontWeight: minWeight };
    }

    const sizeRange = maxSize - minSize;
    const weightRange = maxWeight - minWeight;
    const countRange = maxCount - minCount;
    
    const size = minSize + sizeRange * ((count - minCount) / countRange);
    const weight = minWeight + weightRange * ((count - minCount) / countRange);

    return { fontSize: `${size}px`, fontWeight: Math.round(weight / 100) * 100 };
  };

  const getTagClass = (index) => {
    if (index === 0) return 'skill-tag-rank-1';
    if (index === 1) return 'skill-tag-rank-2';
    if (index === 2) return 'skill-tag-rank-3';
    return ''; // No extra color class for others
  };

  const minMaxStrengths = topStrengths.reduce((acc, [, count]) => ({
    min: Math.min(acc.min, count),
    max: Math.max(acc.max, count)
  }), { min: Infinity, max: -Infinity });

  const minMaxValues = topValues.reduce((acc, [, count]) => ({
    min: Math.min(acc.min, count),
    max: Math.max(acc.max, count)
  }), { min: Infinity, max: -Infinity });

  const minMaxKeywords = topKeywords.reduce((acc, [, count]) => ({
    min: Math.min(acc.min, count),
    max: Math.max(acc.max, count)
  }), { min: Infinity, max: -Infinity });

  // Utility function to shuffle an array
  const shuffleArray = (array) => {
    let currentIndex = array.length,  randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }
    return array;
  };

  // Shuffle the arrays before rendering
  const shuffledStrengths = shuffleArray([...topStrengths]);
  const shuffledValues = shuffleArray([...topValues]);
  const shuffledKeywords = shuffleArray([...topKeywords]);

  // Dynamic height calculation for bar charts
  const calculateChartHeight = (itemCount) => Math.max(itemCount * 40, 300);
  const languageChartHeight = calculateChartHeight(topLanguages.length);
  const certificationChartHeight = calculateChartHeight(topCertifications.length);


  return (
    <div className="dashboard-grid">
      {/* KPIs */}
      <div className="card kpi-card grid-col-span-3">
        <div className="card-title">{t('talentAnalytics.totalTalent', 'Total Talent')}</div>
        <div className="kpi-value">{totalTalent}</div>
        <div className="kpi-label">{t('talentAnalytics.activeProfiles', 'Active profiles in pool')}</div>
      </div>
      <div className="card kpi-card grid-col-span-3">
        <div className="card-title">{t('talentAnalytics.topSkill', 'Top Skill')}</div>
        <div className="kpi-value kpi-value-dynamic">{topSkill.name}</div>
        <div className="kpi-label">{t('talentAnalytics.foundInProfiles', { percentage: topSkill.percentage, defaultValue: `Found in ${topSkill.percentage}% of profiles` })}</div>
      </div>
      <div className="card kpi-card grid-col-span-3">
        <div className="card-title">{t('talentAnalytics.avgExperience', 'Avg. Experience')}</div>
        <div className="kpi-value">{avgExperience} {t('talentAnalytics.yrs', 'yrs')}</div>
        <div className="kpi-label">{t('talentAnalytics.acrossAllCandidates', 'Across all candidates')}</div>
      </div>
      <div className="card kpi-card grid-col-span-3">
        <div className="card-title">{t('talentAnalytics.readyToMove', 'Ready to Move')}</div>
        <div className="kpi-value">{readyToMove}</div>
        <div className="kpi-label">{t('talentAnalytics.activelyLooking', 'Actively looking')}</div>
      </div>

      <div className="card grid-col-span-12">
        <h3 className="card-title">{t('talentAnalytics.idealWorkEnvironment', 'Ideal Work Environment Insights')}</h3>
        <div className="skill-cloud">
            {shuffledKeywords.map(([keyword, count]) => (
              <span 
                key={keyword} 
                className={`skill-cloud-tag ${getTagClass(topKeywords.findIndex(item => item[0] === keyword))}`}
                style={getTagStyle(count, minMaxKeywords.min, minMaxKeywords.max)}
              >
                {keyword}
              </span>
            ))}
        </div>
      </div>

      {/* Skill Distribution Chart */}
      <div className="card grid-col-span-8">
        <h3 className="card-title">
            <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M3 3v18h18"/></svg>
            {t('talentAnalytics.topSkillDistribution', 'Top 10 Skill Distribution')}
        </h3>
        <div className="chart-container">
            <Bar data={skillChartData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', scales: { y: { beginAtZero: true } }, plugins: { legend: { display: false } } }} />
        </div>
      </div>

      {/* Experience Level Chart */}
      <div className="card grid-col-span-4">
        <h3 className="card-title">
            <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M12 2v10h10"/></svg>
            {t('talentAnalytics.experienceLevels', 'Experience Levels')}
        </h3>
        <div className="chart-container">
            <Doughnut data={experienceChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
        </div>
      </div>

      {/* Location Chart */}
      <div className="card grid-col-span-6">
        <h3 className="card-title">
            <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {t('talentAnalytics.locationPreferences', 'Location Preferences')}
        </h3>
        <div className="chart-container">
            <Bar data={locationChartData} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } }, plugins: { legend: { display: false } } }} />
        </div>
      </div>

      {/* Top Strengths */}
      <div className="card grid-col-span-6">
        <h3 className="card-title">
            <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            {t('talentAnalytics.topStrengths', 'Top Strengths')}
        </h3>
        <div className="skill-cloud">
            {shuffledStrengths.map(([strength, count]) => (
              <span 
                key={strength} 
                className={`skill-cloud-tag ${getTagClass(topStrengths.findIndex(item => item[0] === strength))}`}
                style={getTagStyle(count, minMaxStrengths.min, minMaxStrengths.max)}
              >
                {strength}
              </span>
            ))}
        </div>
      </div>

      {/* New Charts */}
      <div className="card grid-col-span-6">
        <h3 className="card-title">{t('talentAnalytics.topLanguages', 'Top Languages')}</h3>
        <div className="chart-container" style={{ height: `${languageChartHeight}px` }}>
            <Bar data={languageChartData} options={languageChartOptions} />
        </div>
      </div>
      <div className="card grid-col-span-6">
        <h3 className="card-title">{t('talentAnalytics.topValues', 'Top Values')}</h3>
        <div className="skill-cloud">
            {shuffledValues.map(([value, count]) => (
              <span 
                key={value} 
                className={`skill-cloud-tag ${getTagClass(topValues.findIndex(item => item[0] === value))}`}
                style={getTagStyle(count, minMaxValues.min, minMaxValues.max)}
              >
                {value}
              </span>
            ))}
        </div>
      </div>
      <div className="card grid-col-span-6">
        <h3 className="card-title">{t('talentAnalytics.topCertifications', 'Top Certifications')}</h3>
        <div className="chart-container" style={{ height: `${certificationChartHeight}px` }}>
            <Bar data={certificationChartData} options={certificationChartOptions} />
        </div>
      </div>
    </div>
  );
}

export default TalentAnalyticsDashboard;
